import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import { Box, Grid, Modal } from '@mui/material';
import { useDispatch } from 'react-redux';
import { showProgress } from '../../redux/actions/DealershipActions';
import CustomPopupTopBar from '../shared/CustomPopupTopBar';
import CustomPopupFooter from '../shared/CustomPopupFooter';
import ResourceCard from './ResourceCard';
import ShowProgress from '../ui/Backdrop';
import ReactDnD from '../common/ReactDnD';
import service from '../../apis/service';
import { apiEndpoints } from '../../constants/apiEndpoints';
import { ErrorToast, notifySuccess } from '../utils/Utils';

const modalStyle = {
  display: 'flex',
  flexDirection: 'column',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '527px',
  height: '90vh',
  backgroundColor: '#F2F4F7',
  boxShadow: 24,
  outline: 'none',
};

const useStyles = makeStyles({
  contentContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '10px',
    backgroundColor: '#F2F4F7',
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: '300px',
  },
  addButton: {
    color: '#622FEE',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '21px',
    textDecoration: 'underline',
    marginLeft: '8px',
  },
  resourcesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
    flex: 1,
  },
  buttonContainer: {
    background: '#FFFFFF',
    height: '64px',
    borderTop: '1px solid #E0E0E0',
    padding: '0 16px',
  },
});

const createEmptyResource = () => ({
  id: `temp-${Date.now()}-${Math.random()}`,
  name: '',
  type: 'file',
  url: '',
  file: null,
  filePreview: '',
  fileName: '',
});

const isSameId = (a, b) => a?.toString() === b?.toString();

const AddResource = ({ isOpen, close, collectionId, fetchCollection }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [resources, setResources] = useState([]);

  const fetchResources = useCallback(async () => {
    dispatch(showProgress(true));
    try {
      const response = await service.get(apiEndpoints.COLLECTION_RESOURCES.replace('COLLECTION_ID', collectionId));

      const mappedResources = (response.resources || []).map((resource) => {
        const type = resource.type || 'file';
        const source = resource.url || '';

        return {
          id: resource.id,
          name: resource.name || '',
          type,
          url: type === 'link' ? source : '',
          file: null,
          filePreview: '',
          fileName: type === 'file' ? source : resource.fileName || '',
        };
      });

      setResources(mappedResources.length === 0 ? [createEmptyResource()] : mappedResources);
    } catch (error) {
      ErrorToast(error);
      setResources([createEmptyResource()]);
    } finally {
      dispatch(showProgress(false));
    }
  }, [collectionId, dispatch]);

  useEffect(() => {
    if (isOpen && collectionId) {
      fetchResources();
    }
  }, [isOpen, collectionId, fetchResources]);

  const handleClose = () => {
    setResources([]);
    close();
  };

  const handleAddResource = () => {
    setResources((prev) => [...prev, createEmptyResource()]);
  };

  const handleResourceUpdate = (resourceId, updatedData) => {
    setResources((prev) =>
      prev.map((resource) => (isSameId(resource.id, resourceId) ? { ...resource, ...updatedData } : resource)),
    );
  };

  const handleResourceDelete = (index) => {
    setResources((prev) => {
      // Use index to find the resource, but verify it exists
      if (index < 0 || index >= prev.length) {
        return prev;
      }
      const updated = prev.filter((_, i) => i !== index);
      return updated.length === 0 ? [createEmptyResource()] : updated;
    });
  };

  const handleDragEnd = (newData) => {
    setResources(newData);
  };

  const handleUpdateResourceOrder = (orderIds) => {
    setResources((prevResources) => {
      const resourceMap = new Map(prevResources.map((r) => [r.id?.toString(), r]));
      const orderedResources = orderIds.map((id) => resourceMap.get(id?.toString())).filter(Boolean);

      if (orderedResources.length === prevResources.length) {
        return orderedResources;
      }
      return prevResources;
    });
  };

  const validateResources = () => {
    const nonEmptyResources = resources.filter((r) => r.name?.trim() || r.url?.trim() || r.file || r.fileName);
    const errors = nonEmptyResources
      .map((resource, index) => {
        if (!resource.name?.trim()) {
          return `Please enter Resource Name for resource ${index + 1}`;
        }
        if (resource.type === 'link' && !resource.url?.trim()) {
          return `Please enter Link for resource ${index + 1}`;
        }
        if (resource.type === 'file' && !resource.file && !resource.fileName) {
          return `Please upload File for resource ${index + 1}`;
        }
        return null;
      })
      .filter(Boolean);

    if (errors.length) {
      ErrorToast(errors[0]);
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateResources()) return;

    dispatch(showProgress(true));
    try {
      const formData = new FormData();
      const nonEmptyResources = resources.filter((r) => r.name?.trim() || r.url?.trim() || r.file || r.fileName);

      nonEmptyResources.forEach((resource, index) => {
        if (resource.type === 'file' && resource.file) {
          formData.append(`source_${index}`, resource.file);
        }
      });

      const resourcesPayload = nonEmptyResources.map((resource) => {
        const payload = {
          name: resource.name.trim(),
          type: resource.type,
          source: resource.type === 'link' ? resource.url.trim() : 'file',
        };

        if (resource.id && !resource.id.toString().startsWith('temp-')) {
          payload.id = resource.id;
        }

        return payload;
      });

      formData.append('resources', JSON.stringify(resourcesPayload));

      await service.post(apiEndpoints.COLLECTION_RESOURCES_SAVE.replace('COLLECTION_ID', collectionId), formData);

      notifySuccess('Resources saved successfully');
      fetchCollection?.();
      handleClose();
    } catch (error) {
      ErrorToast(error);
    } finally {
      dispatch(showProgress(false));
    }
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box sx={modalStyle}>
        <CustomPopupTopBar title="Resources" close={handleClose} />

        <Grid container direction="column" className={classes.contentContainer}>
          <Grid item xs={12} style={{ flex: 1, overflowY: 'auto' }}>
            <div className={classes.resourcesList}>
              <ReactDnD
                data={resources}
                setData={handleDragEnd}
                setModifiedOrderIds={handleUpdateResourceOrder}
                calledFrom="resource"
                id={collectionId}
                renderItem={(resource) => {
                  const index = resources.findIndex((r) => isSameId(r.id, resource.id));
                  const isEmpty =
                    !resource.name?.trim() && !resource.url?.trim() && !resource.file && !resource.fileName;
                  return (
                    <ResourceCard
                      key={resource.id || index}
                      data={resource}
                      setData={(updated) => {
                        handleResourceUpdate(resource.id, updated);
                      }}
                      onDelete={isEmpty ? null : () => handleResourceDelete(index)}
                      expand={index === 0}
                    />
                  );
                }}
                renderClone={(provided, _, rubric) => {
                  const resource = resources[rubric.source.index];
                  const index = resources.findIndex((r) => isSameId(r.id, resource.id));

                  return (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={provided.draggableProps.style}
                    >
                      <ResourceCard
                        key={resource.id || index}
                        data={resource}
                        setData={(updated) => {
                          handleResourceUpdate(resource.id, updated);
                        }}
                        onDelete={() => handleResourceDelete(index)}
                      />
                    </div>
                  );
                }}
              />

              <Typography className={classes.addButton} onClick={handleAddResource}>
                + Add Resource
              </Typography>
            </div>
          </Grid>
        </Grid>

        <div className={classes.buttonContainer}>
          <CustomPopupFooter add={false} onClose={handleClose} saveContest={handleSave} step={1} totalSteps={1} />
        </div>

        <ShowProgress />
      </Box>
    </Modal>
  );
};

export default AddResource;
